import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import { theme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { InvitationsProvider } from './contexts/InvitationsContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { useAuth } from './contexts/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import axios from './api/axios';
import Dashboard from './pages/Dashboard';
import HouseDetail from './pages/HouseDetail';
import HouseContentsPage from './pages/HouseContentsPage';
import Login from './pages/Login';
import Register from './pages/Register';
import { TestAuth } from './pages/TestAuth';
import { CalendarPage } from './pages/CalendarPage';
import { WishlistPage } from './pages/WishlistPage';
import { ContactsPage } from './pages/ContactsPage';
import { InvitationsPage } from './pages/InvitationsPage';
import InvitationLandingPage from './pages/InvitationLandingPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import HouseMembersPage from './pages/HouseMembersPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PublicHomePage from './pages/PublicHomePage';

interface HouseAccessRouteProps {
  children: React.ReactElement<any>;
}

interface HouseAccessResponse {
  host?: {
    id: string;
  };
  coHosts?: Array<{
    id: string;
  }>;
}

const HouseAccessRoute: React.FC<HouseAccessRouteProps> = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const { user, loading } = useAuth();
  const [canManage, setCanManage] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadAccess = async () => {
      if (loading) {
        return;
      }

      if (!id || !user) {
        if (isMounted) {
          setCanManage(false);
          setIsReady(true);
        }
        return;
      }

      try {
        const response = await axios.get<HouseAccessResponse>(`/houses/${id}`);
        const house = response.data;
        const isOwner = house.host?.id === user.id;
        const isCoHost = Boolean(house.coHosts?.some((member) => member.id === user.id));

        if (isMounted) {
          setCanManage(isOwner || isCoHost);
          setIsReady(true);
        }
      } catch {
        if (isMounted) {
          setCanManage(false);
          setIsReady(true);
        }
      }
    };

    setIsReady(false);
    loadAccess();

    return () => {
      isMounted = false;
    };
  }, [id, loading, user]);

  if (!isReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return React.cloneElement(children, {
    isOwner: canManage,
    currentUserId: user?.id,
  });
};

const HomeRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Dashboard />;
  }

  return <PublicHomePage />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <InvitationsProvider>
          <NotificationsProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/test-auth" element={<TestAuth />} />
              <Route path="/invitations/:id" element={<InvitationLandingPage />} />

              {/* Protected routes */}
              <Route
                path="/"
                element={<HomeRoute />}
              />
              <Route
                path="/houses/:id"
                element={
                  <PrivateRoute>
                    <HouseDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/articles"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <HouseContentsPage contentType="guide" />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/activities"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <HouseContentsPage contentType="activities" />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/shops"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <HouseContentsPage contentType="shops" />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/restaurants"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <HouseContentsPage contentType="restaurants" />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/calendar"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <CalendarPage />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/wishlist"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <WishlistPage />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/problems"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <HouseContentsPage contentType="problem" />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/contacts"
                element={
                  <PrivateRoute>
                    <HouseAccessRoute>
                      <ContactsPage />
                    </HouseAccessRoute>
                  </PrivateRoute>
                }
              />
              <Route
                path="/houses/:id/members"
                element={
                  <PrivateRoute>
                    <HouseMembersPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute>
                    <NotificationsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/invitations"
                element={
                  <PrivateRoute>
                    <InvitationsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                }
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </NotificationsProvider>
          </InvitationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
